//
// ProjectModulesWorksheet
//
// Project modules worksheet

import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { usePersistentState } from '../../hooks/usePersistentState'
import { useStore } from '../../stores'

import { HView, Spacer, VView } from '../../appview'
import { Header, Icon, Text } from '../../components'
import { useMousePopover } from '../../hooks/useMousePopover'
import { ModuleActionMenu } from '../../menus'
import { humanReadableDateTime } from '../../utils/text'
import { prettyprint } from '../../utils/utils'

export const ProjectModulesWorksheet = observer(function ProjectModulesWorksheet({
    project,
    moduleName,
    viewkey,
}) {
    const { app, data } = useStore()

    const ActionsHidden = true

    // ContextMenus
    const ActionContextMenu = useMousePopover(ModuleActionMenu)

    const [activeAction, setActiveAction] = useState(null)

    const [keysVisible, setKeysVisible] = usePersistentState(
        viewkey + '/keysVisible',
        true
    )
    const toggleKeysVisibility = () => {
        setKeysVisible(!keysVisible)
    }

    const [actionsVisible, setActionsVisible] = usePersistentState(
        viewkey + '/actionsVisible',
        true
    )
    const toggleActionsVisibility = () => {
        setActionsVisible(!actionsVisible)
    }
    const [actionitemsVisible, setActionitemsVisible] = useState(new Set())
    const toggleActionitemVisibility = key => {
        setActionitemsVisible(prev => {
            if (prev.has(key)) {
                prev.delete(key)
            } else {
                prev.add(key)
            }
            return new Set(prev)
        })
    }
    const setActionitemVisible = key => {
        setActionitemsVisible(prev => {
            if (prev.has(key)) {
                return prev
            }
            prev.add(key)
            return new Set(prev)
        })
    }
    const [logsVisible, setLogsVisible] = usePersistentState(
        viewkey + '/logsVisible',
        true
    )
    const toggleLogsVisibility = () => {
        setLogsVisible(!logsVisible)
    }
    const [logitemsVisible, setLogitemsVisible] = useState(new Set())
    const toggleLogitemVisibility = key => {
        setLogitemsVisible(prev => {
            if (prev.has(key)) {
                prev.delete(key)
            } else {
                prev.add(key)
            }
            return new Set(prev)
        })
    }

    if (!moduleName) {
        return (
            <VView className="worksheet worksheet-settings worksheet-modules">
                <VView grow>
                    <Header>{app.text('Modules')}</Header>
                    <HView>
                        <Icon name="module" size={2} />
                        <Text>{app.text('No module selected.')}</Text>
                    </HView>
                </VView>
                <VView grow></VView>
            </VView>
        )
    }

    const module = data.modules.get(moduleName)
    if (!module) {
        return (
            <VView className="worksheet worksheet-settings worksheet-modules">
                <VView grow>
                    <Header>{app.text('Modules')}</Header>
                    <HView>
                        <Icon name="module" size={2} />
                        <Text>{app.text('No modules registered.')}</Text>
                    </HView>
                </VView>
                <VView grow></VView>
            </VView>
        )
    }

    const onUnpublishAction = () => {
        if (activeAction) {
            module.unpublishAction(activeAction)
            setActiveAction(null)
        }
    }

    const onCancel = () => {}

    const onAction = action => {
        const actions = {
            unpublish_action: onUnpublishAction,
            cancel: onCancel,
        }
        if (!(action in actions)) {
            console.log(`onAction: unhandled action '${action}'`)
            return
        }
        ActionContextMenu.hide()
        actions[action]()
    }

    const Keys =
        module.keys.size === 0 ? (
            <HView>
                <Text>{app.text('No keys set.')}</Text>
            </HView>
        ) : keysVisible ? (
            <div className="module-table module-table-keys">
                <table>
                    <thead>
                        <tr>
                            <td>{app.text('name')}</td>
                            <td>{app.text('value')}</td>
                        </tr>
                    </thead>
                    <tbody>
                        {Array.from(module.keys.entries()).map(([k, v], i) => {
                            return (
                                <tr key={k + '.' + i}>
                                    <td>{k}</td>
                                    <td>{prettyprint(v)}</td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        ) : undefined

    const Actions =
        module.actions.size === 0 ? (
            <HView>
                <Text>{app.text('No actions published.')}</Text>
            </HView>
        ) : actionsVisible ? (
            <div className="module-table module-table-actions">
                <table>
                    <thead>
                        <tr>
                            <td>{app.text('action')}</td>
                            <td>{app.text('configuration')}</td>
                        </tr>
                    </thead>
                    <tbody>
                        {Array.from(module.actions.entries())
                            .sort()
                            .flatMap(([k, v], i) => {
                                const key = k + '.' + i
                                const itemVisible = actionitemsVisible.has(key)
                                const inputs_count = Object.keys(
                                    v['configuration']
                                ).length

                                let rows = [
                                    <tr
                                        key={key}
                                        className={
                                            itemVisible ? 'cc-multiselected' : ''
                                        }
                                    >
                                        <td
                                            style={{ width: '40%' }}
                                            onContextMenu={e => {
                                                setActiveAction(k)
                                                setActionitemVisible(key)
                                                ActionContextMenu.onShow(e)
                                            }}
                                        >
                                            {k}
                                        </td>
                                        <td
                                            onClick={e =>
                                                toggleActionitemVisibility(key)
                                            }
                                        >
                                            <div>
                                                <Icon
                                                    name={
                                                        itemVisible
                                                            ? 'expanded'
                                                            : 'collapsed'
                                                    }
                                                    size={'text'}
                                                />{' '}
                                                <span className="cc-dimmed">
                                                    {app.pluralized_text(
                                                        {
                                                            0: 'No inputs',
                                                            1: '{count} input',
                                                            many: '{count} inputs',
                                                        },
                                                        inputs_count
                                                    )}
                                                </span>
                                            </div>
                                        </td>
                                    </tr>,
                                ]
                                if (itemVisible) {
                                    rows.push(
                                        <tr key={key + '.config'}>
                                            <td></td>
                                            <td>
                                                <code className="user-select">
                                                    {prettyprint(v['configuration'])}
                                                </code>
                                            </td>
                                        </tr>
                                    )
                                }
                                return rows
                            })}
                    </tbody>
                </table>
            </div>
        ) : undefined

    const Logs =
        module.logs.length === 0 ? (
            <HView>
                <Text>{app.text('No logs added.')}</Text>
            </HView>
        ) : logsVisible ? (
            <div className="module-table module-table-logs">
                <table>
                    <thead>
                        <tr>
                            <td>{app.text('timestamp')}</td>
                            <td>{app.text('version')}</td>
                            <td>{app.text('command')}</td>
                            <td>{app.text('status')}</td>
                            <td>{app.text('duration')}</td>
                        </tr>
                    </thead>
                    <tbody>
                        {module.logs.flatMap((log, i) => {
                            const itemVisible = logitemsVisible.has(log.timestamp)
                            const hasOutput = !!log.output
                            let rows = [
                                <tr
                                    key={log.timestamp + '.' + i}
                                    className={itemVisible ? 'cc-multiselected' : ''}
                                >
                                    <td
                                        onClick={e =>
                                            hasOutput
                                                ? toggleLogitemVisibility(log.timestamp)
                                                : undefined
                                        }
                                    >
                                        <Icon
                                            name={
                                                hasOutput
                                                    ? itemVisible
                                                        ? 'expanded'
                                                        : 'collapsed'
                                                    : 'empty'
                                            }
                                            size={'text'}
                                        />
                                        {humanReadableDateTime(
                                            new Date(log.timestamp * 1000)
                                        )}
                                    </td>
                                    <td>{log.version}</td>
                                    <td>{log.command}</td>
                                    <td>{log.status}</td>
                                    <td>
                                        {log.duration > 10
                                            ? log.duration.toFixed(0) + ' s'
                                            : log.duration > 0
                                              ? log.duration.toFixed(1) + ' s'
                                              : ''}
                                    </td>
                                </tr>,
                            ]
                            if (itemVisible) {
                                rows.push(
                                    <tr key={log.timestamp + '.' + i + '.info'}>
                                        <td
                                            colSpan={5}
                                            className="module-table-logitem"
                                        >
                                            <code className="user-select">
                                                {log.output}
                                            </code>
                                        </td>
                                    </tr>
                                )
                            }
                            return rows
                        })}
                    </tbody>
                </table>
            </div>
        ) : undefined

    const keys_count = module.keys.size
    const actions_count = module.actions.size
    const logs_count = module.logs.length

    return (
        <VView className="worksheet worksheet-settings worksheet-modules">
            <VView grow>
                <ActionContextMenu.Panel onAction={onAction} />
                <Header>{app.text('Module ') + moduleName}</Header>
                <HView vcenter gap={5} onClick={toggleKeysVisibility}>
                    <Header className="cc-subheader">{app.text('Keys')}</Header>
                    <HView
                        vcenter
                        gap={5}
                        style={{ padding: 5, paddingLeft: 0, marginBottom: 5 }}
                    >
                        {keys_count > 0 ? (
                            <Icon
                                name={keysVisible ? 'expanded' : 'collapsed'}
                                size={'text'}
                            />
                        ) : undefined}
                        <div className="cc-dimmed">
                            {app.pluralized_text(
                                {
                                    0: 'No keys',
                                    1: '{count} key',
                                    many: '{count} keys',
                                },
                                keys_count
                            )}
                        </div>
                    </HView>
                </HView>
                {Keys ? (
                    <>
                        {Keys}
                        <Spacer size={20} />
                    </>
                ) : undefined}
                {!ActionsHidden ? (
                    <HView vcenter gap={5} onClick={toggleActionsVisibility}>
                        <Header className="cc-subheader">{app.text('Actions')}</Header>
                        <HView
                            vcenter
                            gap={5}
                            style={{ padding: 5, paddingLeft: 0, marginBottom: 5 }}
                        >
                            {actions_count > 0 ? (
                                <Icon
                                    name={actionsVisible ? 'expanded' : 'collapsed'}
                                    size={'text'}
                                />
                            ) : undefined}
                            <div className="cc-dimmed">
                                {app.pluralized_text(
                                    {
                                        0: 'No actions',
                                        1: '{count} action',
                                        many: '{count} actions',
                                    },
                                    actions_count
                                )}
                            </div>
                        </HView>
                    </HView>
                ) : undefined}
                {!ActionsHidden && Actions ? (
                    <>
                        {Actions}
                        <Spacer size={20} />
                    </>
                ) : undefined}
                <HView vcenter gap={5} onClick={toggleLogsVisibility}>
                    <Header className="cc-subheader">{app.text('Logs')}</Header>
                    <HView
                        vcenter
                        gap={5}
                        style={{ padding: 5, paddingLeft: 0, marginBottom: 5 }}
                    >
                        {logs_count > 0 ? (
                            <Icon
                                name={logsVisible ? 'expanded' : 'collapsed'}
                                size={'text'}
                            />
                        ) : undefined}
                        <div className="cc-dimmed">
                            {app.pluralized_text(
                                {
                                    0: 'No log entries',
                                    1: '{count} log',
                                    many: '{count} log entries',
                                },
                                logs_count
                            )}
                        </div>
                    </HView>
                </HView>
                {Logs ? (
                    <>
                        {Logs}
                        <Spacer size={20} />
                    </>
                ) : undefined}
            </VView>
        </VView>
    )
})
